import React, { useState } from 'react'
import { localizedStrings } from '@core/strings'
import {
  Add,
  FactCheckOutlined,
  ArticleOutlined,
  AssignmentOutlined,
  FlagOutlined,
} from '@mui/icons-material'
import { PageUrls } from '@core/page-urls'
import { Button, Menu, MenuItem } from '@common/material'
import { useRouter } from 'next/router'
import { alpha, useThemeProps } from '@mui/material/styles'

export type SidebarNewButtonProps = {
  organisationId: number
  projectId?: number
}

export const SidebarNewButton: React.FC<SidebarNewButtonProps> = ({
  organisationId,
  projectId,
}) => {
  const router = useRouter()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const { menuItemSx } = useThemeProps({
    name: 'Sidebar',
    props: null,
  })

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const menuItems = [
    {
      label: localizedStrings.newPermit,
      icon: <ArticleOutlined sx={{ width: 16, height: 16 }} color='action' />,
      onClick: () => {
        if (projectId) {
          router.push(PageUrls.newPermitProject({ organisationId, projectId }))
        } else {
          router.push(PageUrls.newPermit(organisationId))
        }
      },
      'data-test': 'new-permit-btn',
    },
    {
      label: localizedStrings.newChecklist,
      icon: <FactCheckOutlined sx={{ width: 16, height: 16 }} color='action' />,
      onClick: () => {
        if (projectId) {
          router.push(
            PageUrls.newChecklistProject({ organisationId, projectId }),
          )
        } else {
          router.push(PageUrls.newChecklist(organisationId))
        }
      },
      'data-test': 'new-checklist-btn',
    },
    {
      label: localizedStrings.newInspection,
      icon: <FlagOutlined sx={{ width: 16, height: 16 }} color='action' />,
      onClick: () => {
        if (projectId) {
          router.push(
            PageUrls.newInspectionProject({ organisationId, projectId }),
          )
        } else {
          router.push(PageUrls.newInspection(organisationId))
        }
      },
      'data-test': 'new-inspection-btn',
    },
    {
      label: localizedStrings.newTask,
      icon: (
        <AssignmentOutlined sx={{ width: 16, height: 16 }} color='action' />
      ),
      onClick: () => {
        if (projectId) {
          router.push(PageUrls.newTaskProject({ organisationId, projectId }))
        } else {
          router.push(PageUrls.newTask(organisationId))
        }
      },
      'data-test': 'new-task-btn',
    },
  ]

  return (
    <>
      <Button
        variant='contained'
        color='primary'
        size='small'
        fullWidth
        startIcon={<Add sx={{ width: 16, height: 16 }} />}
        onClick={handleClick}
        data-test='new-dropdown-btn'
        sx={{
          justifyContent: 'flex-start',
          textTransform: 'none',
          pl: 1,
          '& .MuiButton-startIcon': {
            marginLeft: 0,
            marginRight: 0.75,
          },
        }}
      >
        {localizedStrings.create}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        marginThreshold={0}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiMenu-list': { p: 0 },
        }}
        slotProps={{
          paper: {
            sx: {
              p: 0.5,
              mt: 0.5,
              ml: 0,
              width: '234px',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: `0px 4px 16px ${alpha('#000', 0.08)}`,
            },
          },
        }}
      >
        {menuItems.map((item, index) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              item.onClick()
              handleClose()
            }}
            data-test={item['data-test']}
            sx={menuItemSx}
          >
            {item.icon}
            {item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
